import type { MouseEvent } from "react";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import { ScrollableCards } from "../ScrollableCards";
import {
  ArrowRightIcon,
  ArrowSquareOutIcon,
  LightningIcon,
  MapPinIcon,
  RibbonFold,
  RibbonShape,
  StarIcon,
} from "./icons";
import {
  isConnectedFacility,
  isListedFacility,
  type AvailabilityFacility,
  type AvailabilitySlot,
  type BookableFacility,
  type ConnectedFacility,
  type ListedFacility,
} from "./validate";
export type {
  AvailabilityFacility,
  AvailabilityResult,
  AvailabilitySlot,
  BookableFacility,
  ConnectedFacility,
  ListedFacility,
} from "./validate";

// The MCP emits facility-local dates as "yyyy-MM-dd". `new Date("2026-03-05")`
// parses that as UTC midnight — the previous day in US timezones — so split and
// rebuild as a local calendar date.
export function parseSlotDate(date: string): Date | null {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
}

// "TODAY" / "TOMORROW" / weekday name ("WED") for slot pill headers. The slot
// `date` is in the facility's local timezone (server already converted), but
// the widget compares against the user's local "today" — close enough for a
// label that's mostly a glanceable hint, not a contract.
export function formatRelativeDay(date: string, now: Date = new Date()): string {
  const slotDate = parseSlotDate(date);
  if (!slotDate) return "";

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = slotDate.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86_400_000);

  if (diffDays === 0) return "TODAY";
  if (diffDays === 1) return "TOMORROW";
  return slotDate.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
}

export function formatTime12h(time24: string): string {
  const [hStr, min] = time24.split(":");
  let h = Number(hStr);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}

export function formatPricePerHour(cents: number): string {
  const dollars = Math.ceil(cents / 100);
  return `$${dollars}/hr`;
}

/** "1.2 mi". The distance is the server's, not rounded again here. */
export function formatDistance(miles: number): string {
  return `${miles} mi`;
}

/** "4.8", or null when there is no usable rating (missing, zero, out of range). */
export function formatRating(rating: number | null | undefined): string | null {
  if (typeof rating !== "number" || !Number.isFinite(rating) || rating <= 0 || rating > 5) {
    return null;
  }
  return rating.toFixed(1);
}

/** Whole-percent discount of a slot, rounded down so "N% off" never overstates. 0 when not discounted. */
export function slotDiscountPercent(slot: Pick<AvailabilitySlot, "originalPrice" | "discountedPrice">): number {
  if (slot.originalPrice <= 0 || slot.discountedPrice >= slot.originalPrice) return 0;
  // Whole-cent integer math: float division turns an exact 15% into 14.999… → 14.
  const original = Math.round(slot.originalPrice * 100);
  const discounted = Math.round(slot.discountedPrice * 100);
  return Math.floor(((original - discounted) * 100) / original);
}

/**
 * The ribbon's "N% OFF": the smallest discount among the discounted slots, so
 * every discounted slot is at least that much off. 0 (no ribbon) when none is.
 */
export function facilityDiscountPercent(slots: readonly AvailabilitySlot[]): number {
  const percents = slots.map(slotDiscountPercent).filter((pct) => pct > 0);
  return percents.length > 0 ? Math.min(...percents) : 0;
}

interface AvailabilityCardProps {
  facility: BookableFacility;
}

export function RatingChip({ rating }: { rating: number | null | undefined }) {
  const label = formatRating(rating);
  if (!label) return null;
  return (
    <div className="lk-availability-card-rating">
      <span className="lk-availability-card-star">{StarIcon}</span>
      <span>{label}</span>
    </div>
  );
}

interface SlotPillProps {
  slot: AvailabilitySlot;
}

function SlotPill({ slot }: SlotPillProps) {
  const discounted = slotDiscountPercent(slot) > 0;
  return (
    <a
      className={`lk-availability-slot${discounted ? " lk-availability-slot--discounted" : ""}`}
      href={slot.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => handleCardCtaClick(e, slot.url)}
    >
      <div className="lk-availability-slot-head">
        <span className="lk-availability-slot-day">{formatRelativeDay(slot.date)}</span>
        <span className="lk-availability-slot-time">{formatTime12h(slot.startTime)}</span>
      </div>
      <div className="lk-availability-slot-body">
        {discounted && (
          <span className="lk-availability-slot-was">{formatPricePerHour(slot.originalPrice)}</span>
        )}
        <span className="lk-availability-slot-price">
          {formatPricePerHour(slot.discountedPrice)}
        </span>
        {slot.surface && (
          <span className="lk-availability-slot-surface">{slot.surface}</span>
        )}
      </div>
    </a>
  );
}

export function AvailabilityCard({ facility }: AvailabilityCardProps) {
  // Figma shows three slot tiles side-by-side; show up to three and rely on
  // "More availability" for the rest.
  const visibleSlots = facility.slots.slice(0, 3);
  const locationCity = `${facility.city}, ${facility.state}`;
  const discountPercent = facilityDiscountPercent(visibleSlots);

  return (
    <a
      className="lk-availability-card"
      href={facility.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => handleCardCtaClick(e, facility.url)}
    >
      <div className="lk-availability-card-media lk-availability-card-media--bookable">
        <div
          className="lk-availability-card-image"
          style={facility.photoUrl ? { backgroundImage: `url(${facility.photoUrl})` } : undefined}
          role="img"
          aria-label={facility.name}
        />
        <div className="lk-availability-card-instant">
          <span className="lk-availability-card-instant-icon">{LightningIcon}</span>
          <span>Book instantly</span>
        </div>
        <RatingChip rating={facility.rating} />
      </div>
      {discountPercent > 0 && (
        <div className="lk-availability-card-ribbon">
          <span className="lk-availability-card-ribbon-shape">{RibbonShape}</span>
          <span className="lk-availability-card-ribbon-text">{discountPercent}% OFF</span>
          <span className="lk-availability-card-ribbon-fold">{RibbonFold}</span>
        </div>
      )}
      <div className="lk-availability-card-body">
        <div className="lk-availability-card-top">
          <div className="lk-availability-card-name">{facility.name}</div>
          <div className="lk-availability-card-location">
            <span className="lk-availability-card-icon">{MapPinIcon}</span>
            <span>
              {locationCity} - <strong>{formatDistance(facility.distance)}</strong>
            </span>
          </div>
        </div>
        <div className="lk-availability-card-slots">
          {visibleSlots.map((slot) => (
            <SlotPill key={slot.id} slot={slot} />
          ))}
        </div>
        <span className="lk-availability-card-cta">
          More availability
          <span className="lk-availability-card-arrow">{ArrowRightIcon}</span>
        </span>
      </div>
    </a>
  );
}

/**
 * Chip labels for a connected card: each sport, then the total court count
 * ("1 Court" / "N Courts"). Same rules as the CourtsApp app's
 * connectedCardChips: blank sports and a missing, zero, or non-integer count
 * are dropped.
 */
export function connectedCardChips(
  sports: readonly string[] | undefined,
  courtCount: number | null | undefined,
): string[] {
  const chips = (sports ?? []).map((sport) => sport.trim()).filter((sport) => sport.length > 0);
  if (typeof courtCount === "number" && Number.isInteger(courtCount) && courtCount > 0) {
    chips.push(courtCount === 1 ? "1 Court" : `${courtCount} Courts`);
  }
  return chips;
}

/** "Tempe, AZ" and "2.3 mi". Distance is left off when the server sent 0. */
export function connectedLocation(
  facility: Pick<ConnectedFacility, "city" | "state" | "distance">,
): { place: string; distance: string | null } {
  const place = [facility.city, facility.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
  const miles = facility.distance;
  const distance = typeof miles === "number" && miles > 0 ? formatDistance(miles) : null;
  return { place, distance };
}

/** "Place - 2.3 mi" under a connected or listed club's name; null when it has neither part. */
function PlaceLine({ place, distance }: { place: string; distance: string | null }) {
  if (!place && !distance) return null;
  return (
    <div className="lk-availability-card-location lk-availability-card-location--place">
      <span className="lk-availability-card-icon">{MapPinIcon}</span>
      <span>
        <span className="lk-availability-card-place">{place}</span>
        {place && distance ? " - " : null}
        {distance ? <strong>{distance}</strong> : null}
      </span>
    </div>
  );
}

interface ConnectedCardProps {
  facility: ConnectedFacility;
}

/**
 * A connected club books on its own site. Mirrors the app's connected card
 * (docs/listing-tiers.md § Search / map): paying clubs get a full-width
 * "Book here" button; everyone else gets "More info" (the CourtsApp page) and
 * "Visit website". The MCP does not send `paidListing` yet, so until it does
 * every connected club shows the trial links, like the ChatGPT widget.
 */
export function ConnectedCard({ facility }: ConnectedCardProps) {
  const sportChips = connectedCardChips(facility.sports, facility.courtCount);
  // No sports list or court count: fall back to the single `sport` label.
  const chips = sportChips.length === 0 && facility.sport ? [facility.sport] : sportChips;
  const { place, distance } = connectedLocation(facility);
  const openBookingPage = (e: MouseEvent<HTMLAnchorElement>) => {
    // Count the click before leaving (PostHog click stats), like the MCP widget.
    if (facility.clickTrackUrl && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(facility.clickTrackUrl);
    }
    handleCardCtaClick(e, facility.bookingUrl);
  };

  return (
    <div className="lk-availability-card lk-connected-card">
      <a
        className="lk-connected-card-photo-link"
        href={facility.url}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={-1}
        aria-hidden="true"
        onClick={(e) => handleCardCtaClick(e, facility.url)}
      >
        <div className="lk-availability-card-media">
          <div
            className="lk-availability-card-image"
            style={facility.photoUrl ? { backgroundImage: `url(${facility.photoUrl})` } : undefined}
            role="img"
            aria-label={facility.name}
          />
          <RatingChip rating={facility.rating} />
        </div>
      </a>
      <div className="lk-availability-card-body lk-connected-card-panel">
        <a
          className="lk-connected-card-link"
          href={facility.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => handleCardCtaClick(e, facility.url)}
        >
          <div className="lk-availability-card-name">{facility.name}</div>
          <PlaceLine place={place} distance={distance} />
        </a>
        {chips.length > 0 ? (
          <div className="lk-connected-card-chips">
            {chips.map((chip) => (
              <span key={chip} className="lk-connected-card-chip">
                {chip}
              </span>
            ))}
          </div>
        ) : null}
        {facility.paidListing ? (
          <a
            className="lk-connected-card-book"
            href={facility.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Book here at ${facility.name}`}
            onClick={openBookingPage}
          >
            Book here
            <span className="lk-connected-card-out">{ArrowSquareOutIcon}</span>
          </a>
        ) : (
          <div className="lk-connected-card-links">
            <a
              className="lk-connected-card-more"
              href={facility.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`More info about ${facility.name}`}
              onClick={(e) => handleCardCtaClick(e, facility.url)}
            >
              More info
            </a>
            <a
              className="lk-connected-card-visit"
              href={facility.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Visit website for ${facility.name}`}
              onClick={openBookingPage}
            >
              Visit website
              <span className="lk-connected-card-out">{ArrowSquareOutIcon}</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

interface ListedCardProps {
  facility: ListedFacility;
}

/** A listed club has no photo and no booking flow: name, place and a link to its CourtsApp page. */
export function ListedCard({ facility }: ListedCardProps) {
  const { place, distance } = connectedLocation(facility);
  return (
    <a
      className="lk-availability-card lk-listed-card"
      href={facility.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`More info about ${facility.name}`}
      onClick={(e) => handleCardCtaClick(e, facility.url)}
    >
      <div className="lk-availability-card-name">{facility.name}</div>
      <PlaceLine place={place} distance={distance} />
      <span className="lk-availability-card-cta lk-listed-card-cta">
        More info
        <span className="lk-availability-card-arrow">{ArrowRightIcon}</span>
      </span>
    </a>
  );
}

/** Listed cards per stack column. A listed card is short, so three fit in the height of one bookable card. */
export const LISTED_STACK_SIZE = 3;

/**
 * Splits a result into the cards that get their own column (bookable and
 * connected, in the order given) and the listed clubs packed into stack
 * columns of up to LISTED_STACK_SIZE, in the order given. Nothing is dropped.
 */
export function groupListedIntoStacks(facilities: readonly AvailabilityFacility[]): {
  cards: (BookableFacility | ConnectedFacility)[];
  stacks: ListedFacility[][];
} {
  const cards: (BookableFacility | ConnectedFacility)[] = [];
  const listed: ListedFacility[] = [];
  for (const facility of facilities) {
    if (isListedFacility(facility)) listed.push(facility);
    else cards.push(facility);
  }
  const stacks: ListedFacility[][] = [];
  for (let i = 0; i < listed.length; i += LISTED_STACK_SIZE) {
    stacks.push(listed.slice(i, i + LISTED_STACK_SIZE));
  }
  return { cards, stacks };
}

interface AvailabilityCardsProps {
  facilities: AvailabilityFacility[];
}

export function AvailabilityCards({ facilities }: AvailabilityCardsProps) {
  if (facilities.length === 0) return null;
  const { cards, stacks } = groupListedIntoStacks(facilities);
  return (
    <ScrollableCards className="lk-availability-cards">
      {cards.map((facility) =>
        isConnectedFacility(facility) ? (
          <ConnectedCard key={facility.id} facility={facility} />
        ) : (
          <AvailabilityCard key={facility.id} facility={facility} />
        ),
      )}
      {stacks.map((stack) => (
        // One carousel item per stack, so the scroll arrows step a whole column.
        <div key={stack[0].id} className="lk-listed-stack" role="group" aria-label="More clubs">
          {stack.map((facility) => (
            <ListedCard key={facility.id} facility={facility} />
          ))}
        </div>
      ))}
    </ScrollableCards>
  );
}
